import React, { useEffect, useState } from 'react';
import './LegalTracking.css';
import apiClient from '../lib/api';
import { FileText } from 'lucide-react';
import LegalCharts from '../components/LegalCharts';

type LegalDecision = {
  id: number;
  title: string;
  caseid: string;
  decision_date?: string | null;
  pdf_url?: string | null;
  url: string;
  database_id: string;
  created_at?: string;
};

const LegalTracking: React.FC = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<LegalDecision | null>(null);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachments, setAttachments] = useState<any[] | null>(null);
  const [decisions, setDecisions] = useState<LegalDecision[]>([]);
  const [page, setPage] = useState<number>(1);
  const perPage = 6;
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('decision_date');
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  // Fetch decisions when filters change
  useEffect(() => {
    const fetch = async () => {
      try {
        const params: any = {};
        if (search) params.q = search;
        if (sortBy === 'title') params.sort = 'title';
        else if (sortBy === 'case_number') params.sort = 'case_number';
        else params.sort = 'decision_date_desc';
        const data = await apiClient.legal.listDecisions(params);
        setDecisions(data);
        setPage(1); // reset to first page on new query
        setError(null);
      } catch (e: any) {
        setError('Failed to load precedents decisions');
      } finally {
      }
    };
    fetch();
  }, [sortBy, search]);

  // Debounce search input -> search
  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const handleOpenModal = (card: LegalDecision) => {
    setModalData(card);
    setModalOpen(true);
    setAttachmentsOpen(false);
    setAttachments(null);
  };

  const closeModal = () => setModalOpen(false);

  return (
    <div className="legal-page">
      {/* Removed inline loading text to avoid layout shift while typing */}
      {error && (
        <div style={{ padding: '8px 0', color: '#b91c1c' }}>{error}</div>
      )}
      
      {/* Dynamic Charts Section - now renders its header as the main page header with integrated controls */}
      <LegalCharts 
        isMainPageHeader={true}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <div className="legal-table-container">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Case Number</th>
              <th>Decision Date</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            {decisions.slice((page-1)*perPage, (page-1)*perPage + perPage).map((c: LegalDecision, i) => (
              <tr key={i} className="legal-row" onClick={() => handleOpenModal(c)} style={{ cursor: 'pointer' }}>
                <td className="legal-title">{c.title}</td>
                <td className="legal-case-number">{c.caseid || 'N/A'}</td>
                <td className="legal-date">
                  {c.decision_date ? new Date(c.decision_date).toLocaleDateString() : 'N/A'}
                </td>
                <td className="legal-pdf-icon">
                  <button 
                    className="pdf-icon-btn" 
                    onClick={() => { if(c.pdf_url) window.open(c.pdf_url, '_blank'); }}
                    title="View PDF"
                  >
                    <FileText size={20} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {decisions.length === 0 && !error && (
          <div className="legal-empty-state">
            <p>No precedents decisions found. Try adjusting your filters.</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <div className={`modal-overlay ${modalOpen ? 'active' : ''}`} role="dialog" aria-modal="true" aria-hidden={!modalOpen} onClick={(e) => { if (e.currentTarget === e.target) closeModal(); }}>
        <div className="modal-content">
          <div className="modal-header">
            <div className="modal-title">{modalData?.title || 'Decision Details'}</div>
            <button className="close-btn" onClick={closeModal}>Close</button>
          </div>
          <div className="modal-body">
            <div className="legal-chips" style={{ marginBottom: '0.75rem' }}>
              {modalData?.database_id && (
                <span className="legal-chip legal-chip-category">
                  {modalData.database_id === 'onlt' ? 'OLT' : modalData.database_id === 'onltb' ? 'LTB' : modalData.database_id.toUpperCase()}
                </span>
              )}
            </div>
            <div className="legal-meta" style={{ marginBottom: '0.75rem' }}>
              {modalData?.caseid && <span>Case: {modalData.caseid}</span>}
              {modalData?.decision_date && <span>Decision: {new Date(modalData.decision_date).toLocaleDateString()}</span>}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', color: '#334155', marginTop: '1rem' }}>
              <p>View the full decision document using the link below.</p>
            </div>
          </div>
          <div className="modal-actions">
            {modalData?.id && (
              <button
                className="attach-toggle"
                onClick={async () => {
                  const next = !attachmentsOpen;
                  setAttachmentsOpen(next);
                  if (next && attachments == null) {
                    try {
                      const list = await apiClient.legal.listAttachments(String(modalData.id));
                      setAttachments(list);
                    } catch (e) {
                      setAttachments([]);
                    }
                  }
                }}
              >
                {attachmentsOpen ? 'Hide Attachments ▴' : 'Show Attachments ▾'}
              </button>
            )}
            <button className="btn btn-primary" type="button" onClick={()=>{ if(modalData?.pdf_url){ window.open(modalData.pdf_url, '_blank'); } }}>View PDF</button>
          </div>
          {attachmentsOpen && (
            <div className="modal-body" style={{ paddingTop: 0 }}>
              <div className="attach-list">
                {(attachments || []).length === 0 && (
                  <div className="attach-item"><span className="attach-name">No attachments</span></div>
                )}
                {(attachments || []).map((a) => (
                  <div key={a.id} className="attach-item">
                    <span className="attach-name">{a.filename}</span>
                    <div>
                      {a.created_at && <span className="attach-date" style={{ marginRight: '0.5rem' }}>{new Date(a.created_at).toLocaleDateString()}</span>}
                      <button className="btn btn-primary attach-open" onClick={() => window.open(a.url, '_blank')}>View</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pagination - bottom of page, space reserved to avoid layout shift */}
      <div className={`pagination ${decisions.length > perPage ? '' : 'hidden'}`}>
        {decisions.length > perPage && (
          <>
            <button disabled={page===1} onClick={()=>setPage((p)=>Math.max(1,p-1))}>Previous</button>
            {Array.from({length: Math.ceil(decisions.length/perPage)}).map((_, idx)=>{
              const num = idx+1;
              return (
                <button key={num} className={page===num? 'active': ''} onClick={()=>setPage(num)}>{num}</button>
              );
            })}
            <button disabled={page===Math.ceil(decisions.length/perPage)} onClick={()=>setPage((p)=>Math.min(Math.ceil(decisions.length/perPage),p+1))}>Next</button>
          </>
        )}
      </div>
    </div>
  );
};

export default LegalTracking;

